import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ASSETS } from "@/lib/assets";
import { X, Lock, Loader2, Sparkles, Tag, Check, CheckCircle2, Download } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PaymentModal({ readingId, onSuccess, onClose }) {
  const { t, user, lang } = useApp();
  const [processing, setProcessing] = useState(false);
  const [config, setConfig] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // {code, percent, final_amount}
  const [validating, setValidating] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const receiptRef = useRef();

  React.useEffect(() => {
    api.get("/payment/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const base = config?.price_inr ?? 51;
  const finalAmount = applied ? applied.final_amount : base;
  const isFree = finalAmount <= 0;

  const applyCoupon = async () => {
    const code = coupon.trim();
    if (!code) return;
    setValidating(true);
    try {
      const { data } = await api.post("/payment/validate-coupon", { code });
      if (data.valid) {
        setApplied({ code: data.code, percent: data.percent, final_amount: data.final_amount });
        toast.success(`${data.code} · ${data.percent}% ${t.payment.off}`);
      } else {
        setApplied(null);
        toast.error(data.reason === "used" ? t.payment.couponUsedByYou : t.payment.invalidCoupon);
      }
    } catch {
      toast.error(t.payment.invalidCoupon);
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => { setApplied(null); setCoupon(""); };

  const pay = async () => {
    setProcessing(true);
    try {
      const { data: order } = await api.post("/payment/create-order", {
        reading_id: readingId,
        coupon: applied?.code || null,
      });

      // 100% off / free → unlock directly, no gateway
      if (order.free) {
        const { data } = await api.post("/payment/verify", { reading_id: readingId, order_id: order.order_id, base_url: window.location.origin });
        setReceipt(data.receipt);
        setProcessing(false);
        return;
      }

      if (order.live) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load Razorpay"); setProcessing(false); return; }
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "AstroFriend",
          description: t.payment.desc,
          order_id: order.order_id,
          prefill: { name: user?.name, email: user?.email },
          theme: { color: "#F59E0B" },
          handler: async (resp) => {
            try {
              const { data } = await api.post("/payment/verify", {
                reading_id: readingId,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_signature: resp.razorpay_signature,
                base_url: window.location.origin,
              });
              setReceipt(data.receipt);
            } catch {
              toast.error("Verification failed");
            } finally {
              setProcessing(false);
            }
          },
          modal: { ondismiss: () => setProcessing(false) },
        });
        rzp.open();
        return;
      }

      // Mock mode
      await new Promise((r) => setTimeout(r, 1200));
      const { data } = await api.post("/payment/verify", { reading_id: readingId, order_id: order.order_id, base_url: window.location.origin });
      setReceipt(data.receipt);
      setProcessing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
      setProcessing(false);
    }
  };

  const downloadReceipt = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(receiptRef.current, { backgroundColor: "#1a1033", scale: 2 });
      const link = document.createElement("a");
      link.download = `AstroFriend-Receipt-${receipt.receipt_no}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch {
      toast.error("Download failed");
    }
  };

  if (receipt) {
    const dt = new Date(receipt.paid_at).toLocaleString(lang === "hi" ? "hi-IN" : "en-IN");
    const discounted = receipt.percent > 0;
    const Row = ({ label, value, strong }) => (
      <div className="flex items-center justify-between py-2 border-b border-amber-500/10 last:border-0">
        <span className="text-slate-400 text-sm">{label}</span>
        <span className={strong ? "text-amber-200 font-semibold" : "text-slate-200 text-sm"}>{value}</span>
      </div>
    );
    return (
      <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm px-4 py-8 overflow-y-auto" data-testid="payment-receipt">
        <div className="glass-strong rounded-3xl p-6 sm:p-8 max-w-md w-full relative rise-in">
          <button onClick={onSuccess} className="absolute top-4 right-4 text-slate-400 hover:text-amber-300" data-testid="receipt-close">
            <X size={20} />
          </button>
          <div ref={receiptRef} className="rounded-2xl p-6" style={{ background: "linear-gradient(160deg,#1e1440,#160e30)" }}>
            <div className="flex items-center gap-2 mb-5">
              <img src={ASSETS.logo} alt="AstroFriend" className="h-9 w-9 rounded-lg object-cover" />
              <span className="font-serif text-xl text-amber-100">AstroFriend</span>
            </div>
            <div className="flex flex-col items-center text-center mb-5">
              <CheckCircle2 className="text-emerald-400 mb-2" size={40} />
              <h3 className="font-serif text-2xl text-amber-100" data-testid="receipt-success">{t.receipt.success}</h3>
              <p className="text-xs text-slate-400 mt-1">{t.receipt.thankYou}</p>
            </div>
            <div className="space-y-0">
              <Row label={t.receipt.receiptNo} value={receipt.receipt_no} />
              <Row label={t.receipt.date} value={dt} />
              <Row label={t.receipt.item} value={receipt.item} />
              <Row label={t.receipt.customer} value={receipt.customer || receipt.email} />
              {discounted && <Row label={t.receipt.base} value={`₹${receipt.base_amount}`} />}
              {discounted && <Row label={`${t.receipt.discount} (${receipt.coupon} · ${receipt.percent}%)`} value={`- ₹${receipt.base_amount - receipt.amount}`} />}
              <Row label={t.receipt.paid} value={receipt.amount > 0 ? `₹${receipt.amount}` : `₹0 · ${t.receipt.freeLine}`} strong />
              <Row label={t.receipt.paymentId} value={receipt.payment_id === "FREE" ? (lang === "hi" ? "नि:शुल्क" : "Free") : receipt.payment_id} />
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button onClick={downloadReceipt} data-testid="download-receipt-btn" className="btn-ghost-gold rounded-full px-4 py-3 flex items-center justify-center gap-2 text-sm">
              <Download size={16} /> {t.receipt.download}
            </button>
            <button onClick={onSuccess} data-testid="receipt-view-report-btn" className="btn-gold rounded-full flex-1 py-3 flex items-center justify-center gap-2 gold-glow">
              <Sparkles size={16} /> {t.receipt.viewReport}
            </button>
          </div>
          <p className="mt-3 text-[11px] text-slate-500 text-center">{t.payment.refundNote}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm px-4 py-8 overflow-y-auto" data-testid="payment-modal">
      <div className="glass-strong rounded-3xl p-8 max-w-md w-full relative rise-in">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-amber-300" data-testid="payment-close">
          <X size={20} />
        </button>
        <div className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-amber-500/25 to-purple-600/25 border border-amber-500/30 mb-5">
          <Lock className="text-amber-300" size={24} />
        </div>
        <h3 className="font-serif text-2xl text-amber-100">{t.payment.title}</h3>
        <p className="mt-2 text-sm text-slate-400">{t.payment.desc}</p>

        {/* Price */}
        <div className="my-5 flex items-baseline gap-3">
          {applied && !isFree && <span className="text-lg text-slate-500 line-through">₹{base}</span>}
          <span className="font-serif text-4xl text-gradient-gold" data-testid="final-price">
            {isFree ? "₹0" : `₹${finalAmount}`}
          </span>
          {applied && (
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              {applied.percent}% {t.payment.off}
            </span>
          )}
        </div>

        {/* Coupon */}
        <div className="mb-5">
          <label className="text-xs uppercase tracking-widest text-amber-300/70 flex items-center gap-1.5 mb-2">
            <Tag size={13} /> {t.payment.couponLabel}
          </label>
          {applied ? (
            <div className="flex items-center justify-between glass rounded-xl px-4 py-3 border border-emerald-500/30" data-testid="coupon-applied">
              <span className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
                <Check size={16} /> {applied.code}
              </span>
              <button onClick={removeCoupon} data-testid="coupon-remove-btn" className="text-xs text-slate-400 hover:text-rose-300">
                {t.payment.remove}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                placeholder={t.payment.couponPlaceholder}
                data-testid="coupon-input"
                className="flex-1 rounded-xl bg-purple-950/50 border border-amber-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 uppercase tracking-wide"
              />
              <button
                onClick={applyCoupon}
                disabled={validating || !coupon.trim()}
                data-testid="coupon-apply-btn"
                className="btn-ghost-gold rounded-xl px-5 text-sm disabled:opacity-50"
              >
                {validating ? <Loader2 className="animate-spin" size={16} /> : t.payment.apply}
              </button>
            </div>
          )}
        </div>

        <button onClick={pay} disabled={processing} data-testid="pay-now-btn" className="btn-gold w-full rounded-full py-3.5 flex items-center justify-center gap-2 gold-glow disabled:opacity-70">
          {processing ? (
            <><Loader2 className="animate-spin" size={18} /> {t.payment.processing}</>
          ) : isFree ? (
            <><Sparkles size={18} /> {t.payment.freeUnlock}</>
          ) : (
            <><Sparkles size={18} /> {t.payment.pay} ₹{finalAmount}</>
          )}
        </button>

        {config && !config.live && !isFree && (
          <p className="mt-4 text-xs text-amber-200/60 text-center">{t.payment.mockNote}</p>
        )}
        <p className="mt-2 text-[11px] text-slate-500 text-center">{t.payment.refundNote}</p>
        <button onClick={onClose} className="mt-3 w-full text-sm text-slate-500 hover:text-slate-300">{t.payment.cancel}</button>
      </div>
    </div>
  );
}
